/**
 * Manual retry of failed and skipped deliveries from the dashboard (D-51).
 *
 * A retry never replays a snapshot: the worker resolves the credential as it is configured at send time, and
 * deliveries of customers use the customer's current addresses. The preview shows what a retry would do,
 * including which credential would be used now, so a credential that is still broken is caught before sending.
 */
import { z } from "zod";
import { CHANNELS, LIMITS } from "../constants";
import { DELIVERY_ISSUES, RETRY_ACTIONS } from "../issues";
import { IsoDate, Uuid } from "./common";
import { AppRef, DeliveryLogQuery } from "./customers";
import { CredentialAlias } from "./messages";

export const RetryFilter = z
  .strictObject(
    DeliveryLogQuery.pick({
      app_id: true,
      customer_id: true,
      message_id: true,
      channel: true,
      issue: true,
      q: true,
      from: true,
      to: true,
    }).shape,
  )
  .meta({
    id: "RetryFilter",
    description: "The delivery log filters. Only failed and skipped deliveries are considered.",
  });
export type RetryFilter = z.infer<typeof RetryFilter>;

const retryFields = {
  delivery_ids: z
    .array(Uuid)
    .min(1)
    .max(LIMITS.maxRetryIds)
    .optional()
    .meta({ description: "Retry these deliveries. Give either this or `filter`." }),
  filter: RetryFilter.optional(),
  include_unknown_outcome: z.boolean().default(false).meta({
    description:
      "Also resend deliveries whose outcome is unknown because the worker stopped mid-send. They may already have arrived and been charged.",
  }),
  credentials: z
    .partialRecord(z.enum(CHANNELS), CredentialAlias)
    .optional()
    .meta({
      description:
        "Send these channels with this credential alias instead of the one each delivery asked for. Only when every selected delivery belongs to one app.",
      example: { email: "email-backup" },
    }),
};

function oneSelection(req: { delivery_ids?: string[]; filter?: RetryFilter }, ctx: z.RefinementCtx) {
  if (Boolean(req.delivery_ids) === Boolean(req.filter)) {
    ctx.addIssue({ code: "custom", path: [], message: "Give either delivery_ids or filter" });
  }
}

export const RetryRequest = z
  .strictObject(retryFields)
  .superRefine(oneSelection)
  .meta({ id: "RetryRequest" });
export type RetryRequest = z.infer<typeof RetryRequest>;
export type RetryInput = z.input<typeof RetryRequest>;

export const RunRetryRequest = z
  .strictObject({
    ...retryFields,
    after: Uuid.optional().meta({ description: "The `next_cursor` of the previous call." }),
    limit: z.int().min(1).max(LIMITS.retryBatchMax).default(500),
  })
  .superRefine(oneSelection)
  .meta({ id: "RunRetryRequest" });
export type RunRetryRequest = z.infer<typeof RunRetryRequest>;
export type RunRetryInput = z.input<typeof RunRetryRequest>;

export const RetryGroup = z
  .object({
    channel: z.enum(CHANNELS),
    issue: z.enum(DELIVERY_ISSUES),
    action: z.enum(RETRY_ACTIONS),
    count: z.int(),
  })
  .meta({ id: "RetryGroup" });
export type RetryGroup = z.infer<typeof RetryGroup>;

export const RetryCredentialCheck = z
  .object({
    app: AppRef,
    channel: z.enum(CHANNELS),
    deliveries: z.int().meta({ description: "Deliveries of this app and channel that a retry would send." }),
    alias: z
      .string()
      .nullable()
      .meta({ description: "Alias that will be requested. Null means the app's default for the channel." }),
    credential: z
      .object({
        id: Uuid,
        name: z.string(),
        alias: z.string(),
        provider: z.string(),
        updated_at: IsoDate,
      })
      .nullable()
      .meta({ description: "The credential the worker would pick now." }),
    error: z
      .object({ code: z.string(), message: z.string() })
      .nullable()
      .meta({ description: "Why no credential can be picked now. These deliveries would fail again." }),
    unchanged: z.boolean().meta({
      description:
        "Some of these deliveries failed on a credential problem, and the credential picked now is the same one, not edited since. Fix it or choose another before retrying.",
    }),
    options: z
      .array(
        z.object({
          alias: z.string(),
          credential_name: z.string(),
          provider: z.string(),
          usable: z.boolean().meta({ description: "Active, and its provider is not disabled." }),
        }),
      )
      .meta({ description: "Aliases for this channel in the app's credential profile, for `credentials`." }),
  })
  .meta({ id: "RetryCredentialCheck" });
export type RetryCredentialCheck = z.infer<typeof RetryCredentialCheck>;

export const RetryPreview = z
  .object({
    total: z.int().meta({ description: "Failed and skipped deliveries in the selection." }),
    retryable: z
      .int()
      .meta({ description: "Deliveries a retry would resend or recheck with these options." }),
    groups: z.array(RetryGroup),
    credentials: z.array(RetryCredentialCheck),
  })
  .meta({ id: "RetryPreview" });
export type RetryPreview = z.infer<typeof RetryPreview>;

export const RetryResult = z
  .object({
    resent: z
      .int()
      .meta({ description: "Deliveries queued again, including rechecked ones that found an address." }),
    added: z.int().meta({ description: "New deliveries for push devices the customer registered since." }),
    unchanged: z.int().meta({
      description:
        "Deliveries left as they were: the customer still has no usable address, or has opted out since.",
    }),
    next_cursor: Uuid.nullable().meta({
      description: "Pass as `after` to continue. Null when the selection is done.",
    }),
  })
  .meta({ id: "RetryResult" });
export type RetryResult = z.infer<typeof RetryResult>;
